import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, getDocs, getFirestore, query, where } from '@firebase/firestore';
import { app } from '../../firebase.js';
import LeftBar from '../../Components/LeftBar';
import Dashnav from '../../Components/Dashnav';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import Badge from 'react-bootstrap/Badge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowsRotate,
  faMagnifyingGlass,
  faUserXmark,
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import defaultProfilePicture from '../../Images/defprofile.png';
import { toDateValue } from '../../utils/financeWorkflow';

const ATTENDANCE_COLLECTION = 'AttendaceReport';
const NAIROBI_TIME_ZONE = 'Africa/Nairobi';
const DAY_IN_MS = 24 * 60 * 60 * 1000;
const INACTIVITY_OPTIONS = ['All', 'No attendance', '31-60 days', '61-90 days', '90+ days'];

const normalizeText = (value) => String(value || '').trim().toLowerCase();

const formatDateTime = (value, fallback = '—') => {
  const dateValue = toDateValue(value);
  if (!dateValue) {
    return fallback;
  }

  return dateValue.toLocaleString('en-KE', {
    timeZone: NAIROBI_TIME_ZONE,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const formatRelativeDays = (days) => {
  if (days === null || days === undefined) {
    return 'No attendance recorded';
  }

  if (days <= 0) {
    return 'Today';
  }

  if (days === 1) {
    return '1 day ago';
  }

  return `${days} days ago`;
};

const normalizeAttendanceType = (record = {}) => {
  const sessionTitle = normalizeText(record.sessionTitle || record.className);
  const mode = normalizeText(record.mode || record.attendanceMode);
  const hasRoomLink = Boolean(String(record.roomlink || '').trim());
  const hasVirtualSignals = hasRoomLink
    || mode === 'online'
    || sessionTitle.includes('live')
    || sessionTitle.includes('virtual')
    || Boolean(record.startTime || record.endTime);
  const hasPhysicalSignals = sessionTitle === normalizeText('School Check-In')
    || sessionTitle.includes('school check')
    || mode === 'onsite'
    || Boolean(record.locationCheck);

  if (hasVirtualSignals && !hasPhysicalSignals) {
    return 'Virtual';
  }

  if (hasPhysicalSignals) {
    return 'Physical';
  }

  return null;
};

const pickLatestRecord = (records = []) => {
  return [...records].sort((left, right) => {
    const leftTime = left?.checkInTime?.getTime?.() || left?.checkOutTime?.getTime?.() || 0;
    const rightTime = right?.checkInTime?.getTime?.() || right?.checkOutTime?.getTime?.() || 0;
    return rightTime - leftTime;
  })[0] || null;
};

const uniqueRecords = (records = []) => {
  return Array.from(new Map(records.map((record) => [record.id, record])).values());
};

const resolveJoinLabel = (student = {}) => {
  if (student.hasPhysical && student.hasVirtual) {
    return 'Physical + Virtual';
  }

  if (student.hasPhysical) {
    return 'Physical';
  }

  if (student.hasVirtual) {
    return 'Virtual';
  }

  return 'Absent';
};

const getInactivityProfile = (lastActivity) => {
  if (!lastActivity) {
    return {
      bucket: 'No attendance',
      label: 'No attendance',
      tone: 'inactiveLevelBadge inactiveLevelBadge--none',
      rank: 0,
      daysSince: null,
      description: 'No attendance record yet.'
    };
  }

  const daysSince = Math.floor((Date.now() - lastActivity.getTime()) / DAY_IN_MS);

  if (daysSince <= 30) {
    return null;
  }

  if (daysSince <= 60) {
    return {
      bucket: '31-60 days',
      label: '31-60 days',
      tone: 'inactiveLevelBadge inactiveLevelBadge--stale',
      rank: 3,
      daysSince,
      description: 'More than one month without attendance.'
    };
  }

  if (daysSince <= 90) {
    return {
      bucket: '61-90 days',
      label: '61-90 days',
      tone: 'inactiveLevelBadge inactiveLevelBadge--dormant',
      rank: 2,
      daysSince,
      description: 'Two to three months without attendance.'
    };
  }

  return {
    bucket: '90+ days',
    label: '90+ days',
    tone: 'inactiveLevelBadge inactiveLevelBadge--critical',
    rank: 1,
    daysSince,
    description: 'Long-term attendance inactivity.'
  };
};

function InactiveAttendanceStudents() {
  const db = getFirestore(app);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [courseFilter, setCourseFilter] = useState('All');
  const [inactivityFilter, setInactivityFilter] = useState('All');
  const [students, setStudents] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);

  const fetchInactiveStudents = useCallback(async () => {
    setRefreshing(true);

    try {
      const [usersSnapshot, attendanceSnapshot] = await Promise.all([
        getDocs(query(collection(db, 'users'), where('userStatus', '==', 'Student'))),
        getDocs(collection(db, ATTENDANCE_COLLECTION))
      ]);

      const studentRows = [];
      usersSnapshot.forEach((docItem) => {
        const data = docItem.data();
        const userType = normalizeText(data.userType);
        const userStatus = normalizeText(data.userStatus);

        if (userType !== 'student' && userStatus !== 'student') {
          return;
        }

        studentRows.push({
          id: docItem.id,
          userId: data.userId || docItem.id,
          bio: data.bio || data.displayName || data.name || 'Unnamed student',
          course: data.course || 'Course not set',
          cohort: data.cohort || '',
          email: data.userEmail || data.email || '',
          phoneNumber: data.phoneNumber || data.phone || '',
          profilePic: data.profilePic || defaultProfilePicture,
          branchName: data.branchName || data.campus || data.selectedCampus || '',
          admissionNumber: data.admissionNumber || data.prospectAdmissionNumber || '',
          status: data.userStatus || data.userType || 'Student'
        });
      });

      const normalizedRecords = attendanceSnapshot.docs
        .map((docItem) => {
          const data = docItem.data();
          const checkInTime = toDateValue(data.signInTime || data.startTime || data.createdAt);
          const checkOutTime = toDateValue(data.signOutTime || data.endTime);
          const sessionTitle = data.sessionTitle || data.className || 'Class Session';
          const mode = data.mode || data.attendanceMode || (data.roomlink ? 'Online' : 'Onsite');
          const studentId = data.userId || data.studentId || '';
          const attendanceType = normalizeAttendanceType(data);

          if (!attendanceType || !studentId || !checkInTime) {
            return null;
          }

          return {
            id: docItem.id,
            studentId,
            studentName: data.username || data.studentName || 'Student',
            sessionTitle,
            mode,
            attendanceType,
            checkInTime,
            checkOutTime,
            isActive: Boolean(checkInTime && !checkOutTime),
            locationCheck: data.locationCheck || {},
            locationVerified: Boolean(
              data.locationCheck
              && Number.isFinite(Number(data.locationCheck.distanceInMeters))
              && Number(data.locationCheck.distanceInMeters) <= Number(data.locationCheck.radiusInMeters || 200)
            ),
            raw: data
          };
        })
        .filter(Boolean)
        .sort((first, second) => (second.checkInTime?.getTime?.() || 0) - (first.checkInTime?.getTime?.() || 0));

      setStudents(studentRows);
      setAttendanceRecords(normalizedRecords);
    } catch (error) {
      console.error('Failed to load inactive attendance students:', error);
      setStudents([]);
      setAttendanceRecords([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [db]);

  useEffect(() => {
    fetchInactiveStudents();
  }, [fetchInactiveStudents]);

  const inactiveStudents = useMemo(() => {
    const rowsByStudent = new Map();

    attendanceRecords.forEach((record) => {
      if (!rowsByStudent.has(record.studentId)) {
        rowsByStudent.set(record.studentId, []);
      }

      rowsByStudent.get(record.studentId).push(record);
    });

    return students
      .map((student) => {
        const studentRecords = uniqueRecords([
          ...(rowsByStudent.get(student.userId) || []),
          ...(rowsByStudent.get(student.id) || [])
        ]);

        const physicalRecords = studentRecords.filter((record) => record.attendanceType === 'Physical');
        const virtualRecords = studentRecords.filter((record) => record.attendanceType === 'Virtual');
        const lastActivity = pickLatestRecord(studentRecords);
        const inactivityProfile = getInactivityProfile(lastActivity?.checkInTime || lastActivity?.checkOutTime);

        if (!inactivityProfile) {
          return null;
        }

        return {
          ...student,
          studentRecords,
          physicalRecords,
          virtualRecords,
          totalSessions: studentRecords.length,
          lastActivity,
          inactivityProfile,
          inactivityLevel: inactivityProfile.label,
          inactivityBucket: inactivityProfile.bucket,
          inactivityDescription: inactivityProfile.description,
          inactivityDays: inactivityProfile.daysSince,
          hasPhysical: physicalRecords.length > 0,
          hasVirtual: virtualRecords.length > 0,
          joinLabel: resolveJoinLabel({
            hasPhysical: physicalRecords.length > 0,
            hasVirtual: virtualRecords.length > 0
          }),
          lastSeenTime: lastActivity?.checkInTime || lastActivity?.checkOutTime || null,
          sortRank: inactivityProfile.rank,
          sortDays: inactivityProfile.daysSince ?? Number.MAX_SAFE_INTEGER,
          sortTime: lastActivity?.checkInTime?.getTime?.() || lastActivity?.checkOutTime?.getTime?.() || 0,
          sortName: (student.bio || '').toLowerCase()
        };
      })
      .filter(Boolean)
      .filter((student) => {
        if (courseFilter !== 'All' && student.course !== courseFilter) {
          return false;
        }

        if (inactivityFilter !== 'All' && student.inactivityBucket !== inactivityFilter) {
          return false;
        }

        return true;
      })
      .sort((first, second) => {
        const rankDelta = (first.sortRank || 0) - (second.sortRank || 0);
        if (rankDelta !== 0) {
          return rankDelta;
        }

        const timeDelta = (second.sortTime || 0) - (first.sortTime || 0);
        if (timeDelta !== 0) {
          return timeDelta;
        }

        return first.sortName.localeCompare(second.sortName);
      });
  }, [attendanceRecords, courseFilter, inactivityFilter, students]);

  const filteredStudents = useMemo(() => {
    const queryText = searchTerm.trim().toLowerCase();

    if (!queryText) {
      return inactiveStudents;
    }

    return inactiveStudents.filter((student) => {
      const haystack = [
        student.bio,
        student.course,
        student.cohort,
        student.email,
        student.phoneNumber,
        student.admissionNumber,
        student.joinLabel,
        student.inactivityLevel,
        student.lastActivity?.sessionTitle,
        student.lastActivity?.mode
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(queryText);
    });
  }, [inactiveStudents, searchTerm]);

  const uniqueCourses = useMemo(() => {
    return Array.from(new Set(students.map((student) => student.course).filter(Boolean))).sort();
  }, [students]);

  const stats = useMemo(() => {
    const noAttendance = inactiveStudents.filter((student) => student.inactivityBucket === 'No attendance').length;
    const stale = inactiveStudents.filter((student) => student.inactivityBucket === '31-60 days').length;
    const dormant = inactiveStudents.filter((student) => student.inactivityBucket === '61-90 days').length;
    const critical = inactiveStudents.filter((student) => student.inactivityBucket === '90+ days').length;
    const physicalHistory = inactiveStudents.filter((student) => student.hasPhysical).length;
    const virtualHistory = inactiveStudents.filter((student) => student.hasVirtual).length;
    const bothHistory = inactiveStudents.filter((student) => student.hasPhysical && student.hasVirtual).length;

    return {
      inactiveStudents: inactiveStudents.length,
      noAttendance,
      stale,
      dormant,
      critical,
      physicalHistory,
      virtualHistory,
      bothHistory
    };
  }, [inactiveStudents]);

  const latestInactiveStudent = useMemo(() => {
    return [...inactiveStudents]
      .sort((first, second) => (second.lastSeenTime?.getTime?.() || 0) - (first.lastSeenTime?.getTime?.() || 0))[0] || null;
  }, [inactiveStudents]);

  const handleViewStudent = useCallback((studentId) => {
    if (!studentId) {
      return;
    }

    navigate('/view-student', { state: { myData: studentId } });
  }, [navigate]);

  return (
    <div className="mainContainer">
      <LeftBar />
      <div className="theMainContainer">
        <Dashnav />
        <div className="clientsComp applicationsPage attendanceRegisterPage inactiveStudentsPage">
          <section className="appsHeader">
            <div className="attendanceHeaderCopy">
              <p className="appsKicker">Data Analytics</p>
              <h4>Inactive Attendance Students</h4>
              <p className="appsSubtext">
                This page lists enrolled students whose latest class attendance is stale or missing.
                We use the same attendance source as the active register, then show only the students who have gone quiet.
              </p>
              <div className="attendanceHeaderTags">
                <span className="attendanceHeaderTag">No attendance</span>
                <span className="attendanceHeaderTag">31+ days</span>
                <span className="attendanceHeaderTag">Physical / virtual history</span>
              </div>
            </div>
            <div className="appsHeaderStats">
              <div className="appsStatChip">
                <span>Inactive</span>
                <strong>{stats.inactiveStudents}</strong>
              </div>
              <div className="appsStatChip">
                <span>No attendance</span>
                <strong>{stats.noAttendance}</strong>
              </div>
              <div className="appsStatChip">
                <span>31-60 days</span>
                <strong>{stats.stale}</strong>
              </div>
              <div className="appsStatChip">
                <span>61-90 days</span>
                <strong>{stats.dormant}</strong>
              </div>
              <div className="appsStatChip">
                <span>90+ days</span>
                <strong>{stats.critical}</strong>
              </div>
            </div>
          </section>

          <section className="appsFiltersCard attendanceFiltersCard">
            <div className="appsSearch input-group attendanceSearch">
              <span className="input-group-text">
                <FontAwesomeIcon icon={faMagnifyingGlass} />
              </span>
              <Form.Control
                type="search"
                placeholder="Search student, course, cohort, or phone..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>

            <div className="attendanceToolbarRow">
              <Form.Group className="attendanceTypeControl">
                <Form.Label>Course</Form.Label>
                <Form.Select
                  className="filterSelect"
                  value={courseFilter}
                  onChange={(event) => setCourseFilter(event.target.value)}
                >
                  <option value="All">All courses</option>
                  {uniqueCourses.map((course) => (
                    <option key={course} value={course}>
                      {course}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="attendanceTypeControl">
                <Form.Label>Inactivity band</Form.Label>
                <Form.Select
                  className="filterSelect"
                  value={inactivityFilter}
                  onChange={(event) => setInactivityFilter(event.target.value)}
                >
                  {INACTIVITY_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>

            <div className="incomeToolbarMeta attendanceMeta">
              <span className="incomeToolbarText">{filteredStudents.length} visible students</span>
              <span className="incomeInlineDot" />
              <span className="incomeToolbarText">Built from active student records and the shared attendance log</span>
            </div>

            <Button variant="outline-dark" onClick={fetchInactiveStudents} disabled={refreshing}>
              <FontAwesomeIcon icon={faArrowsRotate} className="me-2" />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </section>

          <section className="attendanceMiniSummary">
            <div className="attendanceMiniSummaryItem">
              <span>Inactive students</span>
              <strong>{stats.inactiveStudents}</strong>
            </div>
            <div className="attendanceMiniSummaryItem">
              <span>No attendance</span>
              <strong>{stats.noAttendance}</strong>
            </div>
            <div className="attendanceMiniSummaryItem">
              <span>Both channels</span>
              <strong>{stats.bothHistory}</strong>
            </div>
            <div className="attendanceMiniSummaryItem">
              <span>Physical history</span>
              <strong>{stats.physicalHistory}</strong>
            </div>
            <div className="attendanceMiniSummaryItem">
              <span>Virtual history</span>
              <strong>{stats.virtualHistory}</strong>
            </div>
          </section>

          <div className="attendanceLatestNote">
            {latestInactiveStudent
              ? `Latest inactive touch: ${latestInactiveStudent.bio || 'Student'} was last seen ${formatRelativeDays(latestInactiveStudent.inactivityDays)}.`
              : 'No inactive students were found for the selected filters.'}
          </div>

          {loading ? (
            <div className="incomeStateCard">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading inactive students...</span>
              </Spinner>
              <p>Loading inactive attendance students...</p>
            </div>
          ) : (
            <section className="appsTableCard attendanceTableCard">
              <div className="appsTableHeader">
                <h6>
                  <FontAwesomeIcon icon={faUserXmark} className="me-2" />
                  Inactive students
                </h6>
                <span>{filteredStudents.length} results</span>
              </div>

              <div className="appsTableWrap attendanceTableWrap">
                <Table hover responsive className="appsTable attendanceTable">
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>Course</th>
                      <th>Cohort</th>
                      <th>Last activity</th>
                      <th>Inactivity</th>
                      <th>History</th>
                      <th>Sessions</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStudents.map((student) => (
                      <tr key={student.userId || student.id}>
                        <td>
                          <div className="appsStudentCell attendanceStudentCell">
                            <div className="appDefProfPic attendanceStudentAvatar">
                              <img src={student.profilePic || defaultProfilePicture} alt={student.bio} />
                            </div>
                            <div>
                              <p className="appsStudentName">{student.bio}</p>
                              <span className="appsStudentMeta">
                                {student.email || student.userId}
                                {student.admissionNumber ? ` • ${student.admissionNumber}` : ''}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td>{student.course}</td>
                        <td>{student.cohort || '—'}</td>
                        <td>
                          <div className="attendanceRecordCell">
                            <strong>{student.lastActivity ? formatDateTime(student.lastSeenTime) : 'No attendance recorded'}</strong>
                            <span>{student.lastActivity?.sessionTitle || 'No class sign-in found'}</span>
                          </div>
                        </td>
                        <td>
                          <Badge bg="light" text="dark" className={student.inactivityProfile.tone}>
                            {student.inactivityProfile.label}
                          </Badge>
                        </td>
                        <td>
                          <span className="attendanceTypeBadge attendanceTypeBadge--both">
                            {student.joinLabel}
                          </span>
                        </td>
                        <td>
                          <div className="attendanceRecordCell">
                            <strong>{student.totalSessions}</strong>
                            <span>{formatRelativeDays(student.inactivityDays)}</span>
                          </div>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="appsViewBtn"
                            onClick={() => handleViewStudent(student.userId || student.id)}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                    {filteredStudents.length === 0 && (
                      <tr>
                        <td colSpan="8" className="appsEmptyState">
                          No inactive students match the selected filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

export default InactiveAttendanceStudents;
